import * as React from "react";
import * as questionIds from "@teamprevent/common/questionnaires/questionnaireConfig/genericCase/questionIds";
import { ContextAwareProgressBarQuestionnaireSection } from "@teamprevent/common/questionnaires/components/ContextAwareProgressBarQuestionnaireSection";
import { QuestionConfig, getQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";
import { TextQuestion, } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";

import { QuestionAnswer } from "@teamprevent/common/questionnaires/model";

import { DateQuestion } from "@teamprevent/common/questionnaires/components/questions/DateQuestion";
import { dateOfBirthInRange } from "@teamprevent/common/validators/dateOfBirthInRange";
import { GENDER_OPTIONS } from "@teamprevent/common/utils";
import { createValidator, validators } from "not-valid";

import { employeeDetailSectionId } from "../GenericCaseForm";

interface Props {
    questionConfigs: QuestionConfig[];
    readonly: boolean;
    getAnswerToQuestionWithId(id: string): QuestionAnswer;
    getQuestionMandatoryConfig(questionId: string): boolean;
}

const dateOfBirthValidators = [dateOfBirthInRange];

export const EmployeeDetailsSection: React.FunctionComponent<Props> = (
    {
        questionConfigs,
        readonly,
        getAnswerToQuestionWithId,
        getQuestionMandatoryConfig
    }) => {

    const checkPhoneNumberIsMandatory = (): boolean => {
        return !getAnswerToQuestionWithId(questionIds.WorkPhone) &&
                !getAnswerToQuestionWithId(questionIds.HomePhone) &&
                !getAnswerToQuestionWithId(questionIds.MobilePhone);
    };
    return (
        <ContextAwareProgressBarQuestionnaireSection title="Employee Details" id={employeeDetailSectionId}>
            <TextQuestion
                hideDivider
                questionConfig={getQuestionConfig(questionConfigs, questionIds.ReferredTitle)}
                readonly={readonly}
                validators={[validators.validLength({ max: 25, message: "Maximum of 25 characters only" })]}
            />
            <TextQuestion
                hideDivider
                questionConfig={getQuestionConfig(questionConfigs, questionIds.ReferredFirstName)}
                readonly={readonly}
                mandatory />
            <TextQuestion
                hideDivider
                questionConfig={getQuestionConfig(questionConfigs, questionIds.ReferredLastName)}
                readonly={readonly}
                mandatory
            />
            <DateQuestion
                hideDivider
                questionConfig={getQuestionConfig(questionConfigs, questionIds.ReferredDob)}
                readonly={readonly}
                mandatory={getQuestionMandatoryConfig(questionIds.ReferredDob)}
                validators={dateOfBirthValidators}
            />
            <DropdownQuestion
                questionConfig={getQuestionConfig(questionConfigs, questionIds.ReferredGender)}
                /* eslint-disable-next-line max-len */
                options={GENDER_OPTIONS}
                readonly={readonly}
                mandatory
                hideDivider
            />
            <TextQuestion
                hideDivider
                questionConfig={getQuestionConfig(questionConfigs, questionIds.EmployeeId)}
                readonly={readonly}
                mandatory={getQuestionMandatoryConfig(questionIds.EmployeeId)}
                validators={[ validators.validLength({ max: 20 }) ]}
            />
            <TextQuestion hideDivider questionConfig={getQuestionConfig(questionConfigs, questionIds.JobTitle)} readonly={readonly} mandatory />
            <TextQuestion hideDivider questionConfig={getQuestionConfig(questionConfigs, questionIds.AddressLine1)} readonly={readonly} />
            <TextQuestion hideDivider questionConfig={getQuestionConfig(questionConfigs, questionIds.AddressLine2)} readonly={readonly} />
            <TextQuestion hideDivider questionConfig={getQuestionConfig(questionConfigs, questionIds.AddressLine3)} readonly={readonly} />
            <TextQuestion hideDivider questionConfig={getQuestionConfig(questionConfigs, questionIds.City)} readonly={readonly} />
            <TextQuestion hideDivider questionConfig={getQuestionConfig(questionConfigs, questionIds.County)} readonly={readonly} />
            <TextQuestion
                hideDivider
                questionConfig={getQuestionConfig(questionConfigs, questionIds.Postcode)}
                readonly={readonly}
                mandatory={getQuestionMandatoryConfig(questionIds.Postcode)}
            />
            <TextQuestion
                hideDivider
                questionConfig={getQuestionConfig(questionConfigs, questionIds.PersonalEmailAddress)}
                readonly={readonly}
                mandatory />
            <TextQuestion
                hideDivider questionConfig={getQuestionConfig(questionConfigs, questionIds.ConfirmPersonalEmailAddress)}
                readonly={readonly}
                validators={[createValidator(answer =>
                    answer === getAnswerToQuestionWithId(questionIds.PersonalEmailAddress), "Email addresses do not match")]}
                validationDependencies={[getAnswerToQuestionWithId(questionIds.PersonalEmailAddress)]}
                mandatory
            />
            <TextQuestion
                hideDivider questionConfig={getQuestionConfig(questionConfigs, questionIds.MobilePhone)}
                readonly={readonly}
                validators={[validators.validPhoneNumber(10)]}
                validationDependencies={[getAnswerToQuestionWithId(questionIds.HomePhone), getAnswerToQuestionWithId(questionIds.WorkPhone)]}
                mandatory={checkPhoneNumberIsMandatory()}
                errorMessage="At least one valid telephone number must be provided"
            />
            <TextQuestion
                hideDivider questionConfig={getQuestionConfig(questionConfigs, questionIds.HomePhone)}
                readonly={readonly}
                validators={[validators.validPhoneNumber(10)]}
                validationDependencies={[getAnswerToQuestionWithId(questionIds.MobilePhone), getAnswerToQuestionWithId(questionIds.WorkPhone)]}
                mandatory={checkPhoneNumberIsMandatory()}
                errorMessage="At least one valid telephone number must be provided"
            />
            <TextQuestion
                hideDivider questionConfig={getQuestionConfig(questionConfigs, questionIds.WorkPhone)}
                readonly={readonly}
                validators={[validators.validPhoneNumber(10)]}
                validationDependencies={[getAnswerToQuestionWithId(questionIds.MobilePhone), getAnswerToQuestionWithId(questionIds.HomePhone)]}
                mandatory={checkPhoneNumberIsMandatory()}
                errorMessage="At least one valid telephone number must be provided"
            />
        </ContextAwareProgressBarQuestionnaireSection>
    );
};
